import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { QcLedger, QcLedgerForm, QcLedgerSearchForm } from 'entities/qc_ledger'

type QcLedgersResponse = PagingResponse & {
    results: QcLedger[]
}

export function useFetchQcLedgersApi(searchForm?: Form<QcLedgerSearchForm>): IndexApiSet<QcLedgersResponse> & { execute: () => void } {
    const apiPath = 'qc_ledgers/'
    const api = useIndexApi<QcLedgersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvQcLedgersApi(searchForm: Form<QcLedgerSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'qc_ledgers/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'qc_ledger', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostQcLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<QcLedgerForm>) => void } {
    const apiSet = usePostApi<BaseResponse, QcLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<QcLedgerForm>) => {
        const apiPath = `qc_ledgers/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutQcLedgerApi(): ApiSet<BaseResponse> & { execute: (form: Form<QcLedgerForm>) => void } {
    const apiSet = usePutApi<BaseResponse, QcLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<QcLedgerForm>) => {
        const apiPath = `qc_ledgers/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutQcLedgersApi(): ApiSet<BaseResponse> & { execute: (form: Form<QcLedgerForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, QcLedgerForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<QcLedgerForm[]>) => {
        const apiPath = `qc_ledgers/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteQcLedgerApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `qc_ledgers/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useConfirmQcLedgerApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = usePutApi<BaseResponse, QcLedgerForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number) => {
        const apiPath = `qc_ledgers/${id}/confirm/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

type QcLedgerResponse = BaseResponse & {
    qcLedger?: QcLedger
}

export function useFetchQcLedgerApi(): ApiSet<QcLedgerResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<QcLedgerResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `qc_ledgers/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}
